import { PhoneIcon, TelegramIcon, VKIcon, WhatsAppIcon } from '@/utils/icons'
import type { CrmChatProviderKey } from '@/api/types/crm/crmLeadFunnels.types'
import { WazzupGetFields } from '@pages/Admin/CRMSettings/tabs/GeneralPage/components/WazzupCrmSettings/lib/wazzupIntegration.types'
import WazzupIcon from './WazzupIcon'
import MaxChatIcon from './MaxChatIcon'

export type CRMIntegrationType = 'whatsapp' | 'tgapi' | 'vk' | 'telegram' | 'wapi' | 'phone'

export const CRM_INTEGRATIONS: ReadonlyArray<{
  key: CRMIntegrationType
  icon: React.FC<React.SVGProps<SVGSVGElement>>
  label: string
  color: string
  is_wazzup: boolean
  is_disabled_for_lead?: boolean
}> = [
  {
    key: 'whatsapp',
    icon: WhatsAppIcon,
    label: 'WhatsApp',
    color: 'var(--color-status-success-text)',
    is_wazzup: true,
  },
  {
    key: 'tgapi',
    icon: TelegramIcon,
    label: 'Telegram',
    color: 'var(--color-status-info-text)',
    is_wazzup: true,
  },
  {
    key: 'vk',
    icon: VKIcon,
    label: 'VKontakte',
    color: 'var(--color-status-info-text)',
    is_wazzup: true,
    is_disabled_for_lead: true,
  },
  {
    key: 'telegram',
    icon: TelegramIcon,
    label: 'Telegram Bot',
    color: 'var(--color-status-info-text)',
    is_wazzup: true,
    is_disabled_for_lead: true,
  },
  {
    key: 'wapi',
    icon: WhatsAppIcon,
    label: 'WhatsApp Business API',
    color: 'var(--color-status-success-text)',
    is_wazzup: true,
    is_disabled_for_lead: true,
  },
  {
    key: 'phone',
    icon: PhoneIcon,
    label: 'Phone',
    color: 'var(--color-accent-vivid)',
    is_wazzup: false,
    is_disabled_for_lead: true,
  },
] as const

/** CRM VK: чат с лидом (не Wazzup). */
export const CRM_VK_LEAD_CHAT_INTEGRATION = {
  icon: VKIcon,
  label: 'Чаты ВКонтакте',
  color: 'var(--color-status-info-text)',
} as const

/** CRM MAX: чат с лидом через crm-integration-chat. */
export const CRM_MAX_LEAD_CHAT_INTEGRATION = {
  icon: MaxChatIcon,
  label: 'Чаты MAX',
  color: 'var(--color-accent-vivid)',
} as const

/** CRM Telegram: чат с лидом через crm-integration-chat. */
export const CRM_TG_LEAD_CHAT_INTEGRATION = {
  icon: TelegramIcon,
  label: 'Чаты Telegram',
  color: 'var(--color-status-info-text)',
} as const

/** CRM Wazzup: аккаунт в доке CRM-чатов. */
export const CRM_WAZZUP_LEAD_CHAT_INTEGRATION = {
  icon: WazzupIcon,
  label: 'Чаты Wazzup',
  color: 'var(--color-status-success-text)',
} as const

/** Карточки выбора провайдера в доке CRM-чатов. */
export const CRM_INTEGRATION_CHAT_PROVIDERS: ReadonlyArray<{
  provider: CrmChatProviderKey
  icon: React.FC<React.SVGProps<SVGSVGElement>>
  label: string
  color: string
}> = [
  {
    provider: 'vkontakte',
    ...CRM_VK_LEAD_CHAT_INTEGRATION,
  },
  {
    provider: 'max',
    ...CRM_MAX_LEAD_CHAT_INTEGRATION,
  },
  {
    provider: 'telegram',
    ...CRM_TG_LEAD_CHAT_INTEGRATION,
  },
  {
    provider: 'wazzup',
    ...CRM_WAZZUP_LEAD_CHAT_INTEGRATION,
  },
] as const

export const CRM_INTEGRATION_CHAT_LAUNCHER_LABEL = 'Чаты интеграций'

type FilterByWazzup<T extends readonly any[]> = {
  [K in keyof T]: T[K] extends { is_wazzup: true; key: infer U } ? U : never
}[number]

export type CRMWazzupIntegrationType = FilterByWazzup<typeof CRM_INTEGRATIONS>

const checkActiveWazzupChannel = (
  wazzupIntegrationsData: WazzupGetFields,
  name: string,
) => {
  const val = wazzupIntegrationsData?.channels[name]
  if (val === false || val === undefined || val === null) return false
  return true
}

export const getCrmIntegrations = (wazzupIntegrationsData: WazzupGetFields | undefined) =>
  CRM_INTEGRATIONS.map((i) => ({
    ...i,
    is_active: i.is_wazzup && wazzupIntegrationsData
      ? checkActiveWazzupChannel(wazzupIntegrationsData, i.key)
      : false, // если не wazzup, то не активно (пока) --- "phone"
  }))
